<template>
  <div class="call-summary-page py-3">
    <header class="cs-toolbar">
      <div class="cs-toolbar__title">
        <span class="cs-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Call Summary</h1>
          <p>Every call logged — converted to a sale or not</p>
        </div>
      </div>
      <div class="cs-toolbar__actions">
        <CButton size="sm" class="refresh-btn" :class="{ 'is-loading': isLoading }" :disabled="isLoading" @click="loadCalls">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="refresh-btn__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="cs-alert">{{ errorMessage }}</CAlert>

    <section class="cs-section">
      <header class="cs-section__head">
        <h2>Filters</h2>
        <small class="cs-section__hint">{{ totalLabel }}</small>
      </header>
      <div class="cs-section__body">
        <div class="cs-filters">
          <div class="cs-field cs-field--range">
            <label class="cs-label">Date Range</label>
            <DateRangePicker v-model="dateRange" variant="form" :presets="false" />
          </div>
          <div class="cs-field">
            <label class="cs-label">Call Status</label>
            <AppDropdown v-model="filters.status" :options="statusOptions" placeholder="Any Status" />
          </div>
          <div class="cs-field">
            <label class="cs-label">Call Type</label>
            <AppDropdown v-model="filters.call_type" :options="callTypeOptions" placeholder="Any Type" />
          </div>
          <div class="cs-field">
            <label class="cs-label">Conversion</label>
            <AppDropdown v-model="filters.conversion" :options="conversionOptions" />
          </div>
          <div class="cs-field cs-field--search">
            <label class="cs-label">Search</label>
            <div class="cs-search-row">
              <input v-model.trim="filters.search" type="text" class="cs-input" placeholder="Name or phone…" @keydown.enter="applyFilters">
              <CButton :disabled="isLoading" class="cs-apply-btn" @click="applyFilters">{{ isLoading ? '…' : 'Apply' }}</CButton>
              <CButton :disabled="isLoading" class="cs-reset-btn" @click="resetFilters">Reset</CButton>
            </div>
          </div>
        </div>
      </div>
    </section>

    <BaseTable
      title="Calls"
      :items="tableRows"
      :columns="columns"
      :loading="isLoading"
      :sortable="false"
      :show-count="false"
      empty-text="No calls found for the current filters."
    >
      <template #toolbar>
        <span class="cs-table-hint">Showing {{ pageRangeLabel }}</span>
      </template>

      <template #cell-status="{ row }">
        <span :class="['cs-pill', row.status_class]">{{ row.status_label || '—' }}</span>
      </template>

      <template #cell-converted="{ row }">
        <span v-if="row.converted_order" class="cs-converted">{{ row.converted_order }}</span>
        <span v-else class="cs-converted cs-converted--no">—</span>
      </template>

      <template #cell-comments="{ value }">
        <span class="cs-comments" :title="value || ''">{{ value || '—' }}</span>
      </template>
    </BaseTable>

    <div v-if="!isLoading && calls.length && lastPage > 1" class="cs-pagination-bar">
      <span class="cs-pagination-info">{{ pageRangeLabel }}</span>
      <AppPager :current-page="currentPage" :last-page="lastPage" @change="goToPage" />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import DateRangePicker from '@/components/DateRangePicker.vue'
import BaseTable from '@/components/BaseTable.vue'
import AppPager from '@/components/AppPager.vue'

export default {
  name: 'CallSummary',
  components: { AppDropdown, DateRangePicker, BaseTable, AppPager },
  data () {
    const today = new Date().toISOString().slice(0, 10)
    const thirtyDaysAgo = new Date(Date.now() - 29 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
    return {
      isLoading: false,
      errorMessage: '',
      calls: [],
      total: 0,
      currentPage: 1,
      lastPage: 1,
      perPage: 25,
      statusOptions: [],
      callTypeOptions: [],
      conversionOptions: [
        { value: '', label: 'All Calls' },
        { value: 'converted', label: 'Converted to Sale' },
        { value: 'not_converted', label: 'Call Log Only' }
      ],
      filters: {
        fromDate: thirtyDaysAgo,
        toDate: today,
        status: '',
        call_type: '',
        conversion: '',
        search: ''
      },
      columns: [
        { key: 'date_time', label: 'Date / Time' },
        { key: 'contact', label: 'Contact' },
        { key: 'phone', label: 'Phone' },
        { key: 'call_type', label: 'Type' },
        { key: 'status', label: 'Status' },
        { key: 'duration', label: 'Duration', align: 'right' },
        { key: 'caller', label: 'Caller' },
        { key: 'converted', label: 'Converted' },
        { key: 'comments', label: 'Comments', width: '280px' }
      ]
    }
  },
  computed: {
    totalLabel () {
      return this.total ? `${this.total} total` : ''
    },
    pageRangeLabel () {
      if (!this.calls.length) return '0'
      const start = (this.currentPage - 1) * this.perPage + 1
      const end = start + this.calls.length - 1
      return `${start}–${end} of ${this.total}`
    },
    dateRange: {
      get () {
        return { fromDate: this.filters.fromDate, toDate: this.filters.toDate }
      },
      set (value) {
        this.filters.fromDate = (value && value.fromDate) || ''
        this.filters.toDate = (value && value.toDate) || ''
      }
    },
    tableRows () {
      return this.calls.map((call) => ({
        id: call.id,
        date_time: this.formatDateTime(call.created_at),
        contact: (call.contact && call.contact.name) || '',
        phone: (call.contact && call.contact.phone) || '',
        call_type: this.formatLabel(call.call_type),
        status: this.formatLabel(call.status),
        status_label: this.formatLabel(call.status),
        status_class: this.statusClass(call.status),
        duration: this.formatDuration(call.duration),
        caller: (call.user && call.user.name) || '',
        converted: (call.order && call.order.order_number) || '',
        converted_order: (call.order && call.order.order_number) || '',
        comments: call.comments || ''
      }))
    }
  },
  created () {
    this.loadBootstrapData()
    this.loadCalls()
  },
  methods: {
    formatLabel (value) {
      return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    },
    formatDateTime (timestamp) {
      if (!timestamp) return ''
      const ms = typeof timestamp === 'number' ? timestamp * 1000 : Date.parse(timestamp)
      if (!Number.isFinite(ms)) return ''
      const d = new Date(ms)
      const pad = (n) => String(n).padStart(2, '0')
      return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    formatDuration (seconds) {
      const total = Math.max(0, Number(seconds || 0))
      if (!total) return '—'
      const minutes = Math.floor(total / 60)
      const remainder = total % 60
      return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`
    },
    statusClass (status) {
      const s = String(status || '').toLowerCase()
      if (s.includes('sold') || s === 'closed') return 'is-success'
      if (s.includes('not interested')) return 'is-muted'
      if (s.includes('not attended')) return 'is-warn'
      if (s.includes('feedback') || s.includes('follow') || s.includes('call_again')) return 'is-info'
      return 'is-default'
    },
    buildDropdown (entries) {
      return (entries || []).map((entry) => ({
        value: entry.slug || entry.value || entry.id || '',
        label: entry.name || entry.text || entry.slug || ''
      })).filter((entry) => entry.value || entry.label)
    },
    loadBootstrapData () {
      axios.get(`${this.$apiAdress}/api/order_form_data`)
        .then((response) => {
          const data = response.data || {}
          const dropdown = data.drop_down_items || {}
          this.callTypeOptions = [{ value: '', label: 'Any Type' }, ...this.buildDropdown(dropdown.call_type)]
          this.statusOptions = [{ value: '', label: 'Any Status' }, ...this.buildDropdown(dropdown.call_status)]
        })
        .catch((error) => {
          console.log(error)
        })
    },
    loadCalls (page = this.currentPage) {
      this.errorMessage = ''
      this.isLoading = true
      const payload = {
        page,
        per_page: this.perPage,
        fromDate: this.filters.fromDate || undefined,
        toDate: this.filters.toDate || undefined,
        status: this.filters.status || undefined,
        call_type: this.filters.call_type || undefined,
        conversion: this.filters.conversion || undefined,
        search: this.filters.search || undefined
      }
      axios.post(`${this.$apiAdress}/api/calls/full_listing`, payload)
        .then((response) => {
          const data = response.data || {}
          this.calls = Array.isArray(data.data) ? data.data : []
          this.currentPage = Number(data.current_page || 1)
          this.lastPage = Number(data.last_page || 1)
          this.total = Number(data.total || 0)
          this.perPage = Number(data.per_page || this.perPage)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = error?.response?.data?.message || 'Unable to load call summary.'
          this.calls = []
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    applyFilters () {
      this.currentPage = 1
      this.loadCalls(1)
    },
    resetFilters () {
      const today = new Date().toISOString().slice(0, 10)
      const thirtyDaysAgo = new Date(Date.now() - 29 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
      this.filters = { fromDate: thirtyDaysAgo, toDate: today, status: '', call_type: '', conversion: '', search: '' }
      this.currentPage = 1
      this.loadCalls(1)
    },
    goToPage (page) {
      if (page < 1 || page > this.lastPage) return
      this.loadCalls(page)
    }
  }
}
</script>

<style scoped>
.call-summary-page {
  padding: 0.25rem 0;
}

.cs-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.cs-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.cs-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.cs-toolbar__title p {
  margin: 0.1rem 0 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.cs-toolbar__accent {
  width: 4px;
  height: 32px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
}

.cs-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.cs-toolbar__actions >>> .btn.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.cs-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.cs-toolbar__actions >>> .btn.refresh-btn:disabled {
  opacity: 1;
  background: #f8fafc;
  color: #4b5563;
  cursor: progress;
}

.refresh-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
  transition: transform 0.4s ease;
}

.cs-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) .refresh-btn__icon {
  transform: rotate(60deg);
}

.cs-toolbar__actions >>> .btn.refresh-btn.is-loading .refresh-btn__icon {
  animation: cs-spin 0.9s linear infinite;
}

@keyframes cs-spin {
  to { transform: rotate(360deg); }
}

.cs-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

.cs-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

.cs-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.cs-section__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.cs-section__hint {
  color: #6b7280;
  font-size: 0.78rem;
}

.cs-section__body {
  padding: 1rem;
}

.cs-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.cs-input {
  width: 100%;
  height: 36px;
  padding: 0.3rem 0.6rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #2f3640;
  background: #ffffff;
}

.cs-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.cs-filters {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.85rem;
}

.cs-field {
  display: flex;
  flex-direction: column;
}

.cs-field--range {
  grid-column: span 2;
  min-width: 280px;
}

.cs-field--search {
  grid-column: 1 / -1;
}

.cs-search-row {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.cs-search-row .cs-input {
  flex: 1 1 220px;
  min-width: 200px;
}

.cs-search-row >>> .btn {
  height: 36px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 0 1rem;
}

.cs-search-row >>> .btn.cs-apply-btn {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.cs-search-row >>> .btn.cs-apply-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.cs-search-row >>> .btn.cs-apply-btn:disabled {
  opacity: 0.75;
  cursor: progress;
}

.cs-search-row >>> .btn.cs-reset-btn {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #4f5560;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}

.cs-search-row >>> .btn.cs-reset-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.cs-table-hint {
  font-size: 0.78rem;
  color: #6b7280;
}

.cs-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
}

.cs-pill.is-success {
  background: #dff5ea;
  color: #0e5b46;
}

.cs-pill.is-info {
  background: #e0edff;
  color: #1e40af;
}

.cs-pill.is-warn {
  background: #fef3c7;
  color: #92400e;
}

.cs-pill.is-muted {
  background: #f1f5f9;
  color: #64748b;
}

.cs-pill.is-default {
  background: #eef1f7;
  color: #4b5563;
}

.cs-converted {
  font-weight: 600;
  color: #0e5b46;
}

.cs-converted--no {
  color: #9ca3af;
  font-weight: 500;
}

.cs-comments {
  display: inline-block;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.cs-pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.85rem;
}

.cs-pagination-info {
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
}
</style>
